import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BaseFormProps } from '../../../types/FormTypes';
import { 
  Home, 
  User, 
  MapPin, 
  Calendar,
  Key,
  KeyRound,
  Shield,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { sendEnquiry, fetchEnquiryData } from '../../../utils/properties';
import useTokenStore from '../../../store/store';
import { fetchUserProfile } from '../../../utils/user';

// Additional Product Form Data Interface
export interface AdditionalProductFormData {
  // Access Type Selection
  accessType: 'somebodyPresent' | 'keyPickUp' | 'keySafe';
  
  // Property Address
  propertyAddress: {
    address1: string;
    address2: string;
    postcode: string;
  };
  
  // Property Details
  propertyDetails: {
    propertyType: 'residential' | 'commercial';
    numberOfBedrooms: string;
    propertyFloorArea: string;
    customerType: 'homeowner' | 'tenant' | 'landlord' | 'agent';
    customerName: string;
    isInhabited: boolean;
    furnishedState: '1' | '2' | '3'; // 1=Unfurnished, 2=PartFurnished, 3=Furnished
  };
  
  // Contact Details (Person paying for service)
  contactDetails: {
    title: string;
    firstName: string;
    surname: string;
    emailAddress: string;
    phoneNumber: string;
  };
  
  // Access Contact Details (for property access)
  accessContactDetails: {
    title: string;
    firstName: string;
    surname: string;
    emailAddress: string;
    phoneNumber1: string; // Secondary number (can be landline)
    phoneNumber2: string; // Mobile number (mandatory when access contact provided)
  };
  
  // Key Pickup Details (only for keyPickUp access type)
  keyPickUpAddress?: {
    address1: string;
    address2: string;
    postcode: string;
  };
  
  keyPickUpContactDetails?: {
    title: string;
    firstName: string;
    surname: string;
    emailAddress: string;
    phoneNumber: string;
  };
  
  // Key Safe Details (only for keySafe access type)
  keyCode?: string;
  
  // Appointment Details
  appointmentDetails: {
    asap: boolean;
    preferredDate?: string;
    preferredTime?: string;
  };
  
  // Additional Information
  notes: string;
  phoneNumber: string;
  productId: string;
  createdAt: string;
  formModalUsed?: boolean;
  formCompleted?: boolean;
}

// Props interface extending BaseFormProps
interface AdditionalProductFormProps extends BaseFormProps<AdditionalProductFormData> {
  productId: string;
  serviceName: string;
}

const AdditionalProductForm: React.FC<AdditionalProductFormProps> = ({
  initialData,
  errors = {},
  isSubmitting = false,
  onSubmit,
  onCancel,
  themes,
  productId,
  serviceName
}) => {
  const token = useTokenStore((state) => state.token);
  // Current step state
  const [currentStep, setCurrentStep] = useState<number>(1);
  
  // Local validation errors
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});
  
  // Form data state with default values
  const [formData, setFormData] = useState<AdditionalProductFormData>({
    accessType: 'somebodyPresent', // Default selection
    propertyAddress: {
      address1: '',
      address2: '',
      postcode: ''
    },
    propertyDetails: {
      propertyType: 'residential',
      numberOfBedrooms: '',
      propertyFloorArea: '0', // Default as per API requirement
      customerType: 'homeowner',
      customerName: '',
      isInhabited: true,
      furnishedState: '2'
    },
    contactDetails: {
      title: '',
      firstName: '',
      surname: '',
      emailAddress: '',
      phoneNumber: ''
    },
    accessContactDetails: {
      title: '',
      firstName: '',
      surname: '',
      emailAddress: '',
      phoneNumber1: '',
      phoneNumber2: ''
    },
    appointmentDetails: {
      asap: true
    },
    notes: '',
    phoneNumber: '',
    productId: '',
    createdAt: '',
    formModalUsed: false,
    formCompleted: false,
    ...initialData
  });

  // Get token from store
  const getToken = useTokenStore((state) => state.token);
  const hasLoadedProfile = useRef(false);

  // Load user profile function
  const loadUserProfile = useCallback(async () => {
    try {
      const currentToken = getToken;
      if (!currentToken) {
        console.log('❌ No token available for profile loading');
        return;
      }

      // Check if form already has data from enquiry
      const hasExistingData = formData.contactDetails.emailAddress || formData.contactDetails.firstName;
      if (hasExistingData) {
        console.log('⏭️ Form already has data, skipping profile load');
        return;
      }

      console.log('📡 Fetching user profile data...');
      const userProfile = await fetchUserProfile(currentToken);
      
      if (userProfile) {
        console.log('✅ Auto-populating form with user profile:', userProfile);
        setFormData((prev: AdditionalProductFormData) => {
          const updatedData = {
            ...prev,
            contactDetails: {
              ...prev.contactDetails,
              firstName: userProfile.firstName || prev.contactDetails.firstName,
              surname: userProfile.lastName || prev.contactDetails.surname,
              emailAddress: userProfile.email || prev.contactDetails.emailAddress,
              phoneNumber: userProfile.phone || prev.contactDetails.phoneNumber,
            }
          };
          console.log('📝 Updated form data:', updatedData.contactDetails);
          return updatedData;
        });
      } else {
        console.log('❌ No user profile returned from fetchUserProfile');
      }
    } catch (error) {
      console.error('❌ Failed to load user profile:', error);
    }
  }, [getToken, formData.contactDetails.emailAddress, formData.contactDetails.firstName]);

  // Load enquiry data function
  const loadEnquiryData = async () => {
    try {
      const enquiryId = sessionStorage.getItem('currentEnquiryId');
      
      if (!enquiryId) {
        console.log('No enquiry ID found in session storage');
        return;
      }

      console.log('Fetching enquiry data for ID:', enquiryId);
      const enquiryData = await fetchEnquiryData(enquiryId, token);
      
      if (enquiryData) {
        console.log('✅ Enquiry data loaded, prepopulating form');
        
        // Prepopulate form with enquiry data
        setFormData(prev => ({
          ...prev,
          contactDetails: {
            ...prev.contactDetails,
            emailAddress: enquiryData.email || '',
            firstName: enquiryData.firstName || '',
            surname: enquiryData.lastName || '',
            phoneNumber: enquiryData.phoneNumber || ''
          }
        }));
        
        console.log('Form prepopulated with:', {
          emailAddress: enquiryData.email,
          firstName: enquiryData.firstName,
          surname: enquiryData.lastName,
          phoneNumber: enquiryData.phoneNumber
        });
        
        // Clear the enquiry ID from session storage to prevent reuse
        sessionStorage.removeItem('currentEnquiryId');
      } else {
        console.log('❌ Failed to load enquiry data');
      }
    } catch (error) {
      console.error('Error loading enquiry data:', error);
    }
  };

  // Auto-populate user data if available
  useEffect(() => {
    console.log('🔄 AdditionalProductForm useEffect triggered');
    console.log('📊 Current form data:', formData.contactDetails);
    console.log('🔍 hasLoadedProfile.current:', hasLoadedProfile.current);
    console.log('🔑 User token exists:', !!getToken);
    
    // Only fetch if user is authenticated, haven't already loaded the profile, and don't have user data
    const hasUserData = formData.contactDetails.firstName || 
                       formData.contactDetails.surname || 
                       formData.contactDetails.emailAddress || 
                       formData.contactDetails.phoneNumber;
    
    console.log('🔍 hasUserData:', hasUserData);
    console.log('🔍 hasLoadedProfile.current:', hasLoadedProfile.current);
    console.log('🔑 User is authenticated:', !!getToken);
    
    if (getToken && !hasLoadedProfile.current && !hasUserData) {
      console.log('🚀 Loading user profile...');
      hasLoadedProfile.current = true;
      loadUserProfile();
    } else if (!getToken && !hasUserData) {
      // User not logged in - check for enquiry data
      console.log('👤 User not logged in - checking for enquiry data...');
      loadEnquiryData();
    } else {
      console.log('⏭️ Skipping profile load - not authenticated, already loaded, or has data');
    }
  }, [getToken, formData.contactDetails, loadUserProfile]); // Add loadUserProfile to dependencies

  // Update form data handler
  const updateFormData = useCallback((field: string, value: any) => {
    setFormData(prev => {
      const keys = field.split('.');
      const newData = { ...prev };
      let current: any = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!current[key]) current[key] = {};
        current = current[key];
      }
      
      const lastKey = keys[keys.length - 1];
      current[lastKey] = value;
      
      return newData;
    });
    
    // Clear error when user starts typing
    if (localErrors[field]) {
      setLocalErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [localErrors]);

  // Validation function
  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};
    
    switch (step) {
      case 1: // Access Type Selection - No validation needed, just selection
        break;
        
      case 2: // Property Address & Details
        const { address1, postcode } = formData.propertyAddress;
        const { numberOfBedrooms, customerName } = formData.propertyDetails;
        
        if (!address1.trim()) newErrors['propertyAddress.address1'] = 'Property address is required';
        if (!postcode.trim()) newErrors['propertyAddress.postcode'] = 'Postcode is required';
        if (!numberOfBedrooms.trim()) newErrors['propertyDetails.numberOfBedrooms'] = 'Number of bedrooms is required';
        if (!customerName.trim()) newErrors['propertyDetails.customerName'] = 'Customer name is required';
        break;
        
      case 3: // Contact Details
        const { title, firstName, surname, emailAddress, phoneNumber } = formData.contactDetails;
        
        if (!title.trim()) newErrors['contactDetails.title'] = 'Title is required';
        if (!firstName.trim()) newErrors['contactDetails.firstName'] = 'First name is required';
        if (!surname.trim()) newErrors['contactDetails.surname'] = 'Surname is required';
        if (!emailAddress.trim() || !emailAddress.includes('@')) {
          newErrors['contactDetails.emailAddress'] = 'Valid email is required';
        }
        if (!phoneNumber.trim() || phoneNumber.length < 10) {
          newErrors['contactDetails.phoneNumber'] = 'Valid phone number is required';
        }
        break;
        
      case 4: // Access Details
        if (formData.accessType !== 'somebodyPresent') {
          const { title: accessTitle, firstName: accessFirstName, surname: accessSurname, 
                  emailAddress: accessEmail, phoneNumber2 } = formData.accessContactDetails;
          
          if (!accessTitle.trim()) newErrors['accessContactDetails.title'] = 'Access contact title is required';
          if (!accessFirstName.trim()) newErrors['accessContactDetails.firstName'] = 'Access contact first name is required';
          if (!accessSurname.trim()) newErrors['accessContactDetails.surname'] = 'Access contact surname is required';
          if (!accessEmail.trim() || !accessEmail.includes('@')) {
            newErrors['accessContactDetails.emailAddress'] = 'Valid access contact email is required';
          }
          if (!phoneNumber2.trim() || phoneNumber2.length < 10) {
            newErrors['accessContactDetails.phoneNumber2'] = 'Valid mobile number is required for access contact';
          }
          
          // Additional validation based on access type
          if (formData.accessType === 'keyPickUp') {
            const pickup = formData.keyPickUpAddress;
            const pickupContact = formData.keyPickUpContactDetails;
            
            if (!pickup?.address1?.trim()) newErrors['keyPickUpAddress.address1'] = 'Key pickup address is required';
            if (!pickup?.postcode?.trim()) newErrors['keyPickUpAddress.postcode'] = 'Key pickup postcode is required';
            if (!pickupContact?.firstName?.trim()) newErrors['keyPickUpContactDetails.firstName'] = 'Pickup contact name is required';
            if (!pickupContact?.phoneNumber?.trim()) newErrors['keyPickUpContactDetails.phoneNumber'] = 'Pickup contact phone is required';
          }
          
          if (formData.accessType === 'keySafe' && !formData.keyCode?.trim()) {
            newErrors['keyCode'] = 'Key safe code is required';
          }
        }
        break;
        
      case 5: // Final Review - No additional validation needed
        break;
    }
    
    setLocalErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation handlers
  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
      // Scroll to top of the form container
      const formContainer = document.querySelector('.flex-1.overflow-y-auto');
      if (formContainer) {
        formContainer.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    // Scroll to top of the form container
    const formContainer = document.querySelector('.flex-1.overflow-y-auto');
    if (formContainer) {
      formContainer.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Submit handler - Updated to use existing pattern
  const handleSubmit = async () => {
    if (validateStep(currentStep)) {
      try {
        // Extract contact details for the enquiry
        const { contactDetails } = formData;
        const name = `${contactDetails.title} ${contactDetails.firstName} ${contactDetails.surname}`;
        const email = contactDetails.emailAddress;
        const phoneNumber = contactDetails.phoneNumber;

        // Use the same sendEnquiry function as other products
        const enquiryData = {
          name: name.trim(),
          email: email.trim(),
          phoneNumber: phoneNumber.trim(),
          productId: productId,
          userId: undefined, // Changed from null to undefined
          websiteName: themes?.name || 'Whitelabel Form',
          websiteURL: themes?.website || window.location.href,
          serviceOwnService: false, // Additional products are not own service
          calendarLinkUsed: null,
          calendarCompleted: false,
          formModalUsed: true,
          formCompleted: true,
          formData: JSON.stringify(formData),
          updateExisting: false
        };

        console.log('📋 Sending Additional Product enquiry:', {
          productId,
          serviceName,
          name,
          email
        });

        const result = await sendEnquiry(enquiryData);
        
        if (result?.status === 200 || result?.message?.toUpperCase() === "OK") {
          console.log('✅ Additional Product enquiry saved and emails sent successfully');
          onSubmit(formData, false); // No payment required
        } else {
          throw new Error("Failed to save enquiry");
        }
      } catch (error) {
        console.error('❌ Additional Product enquiry error:', error);
        // Handle error - you might want to show an error message to the user
        alert("An error occurred while submitting your request. Please try again later.");
      }
    }
  };

  // Combine local and parent errors
  const allErrors = { ...localErrors, ...errors };

  // Helper function to get field value from nested form data
  const getFieldValue = (field: string): any => {
    const keys = field.split('.');
    let current: any = formData;
    
    for (const key of keys) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key];
      } else {
        return '';
      }
    }
    
    return current || '';
  };

  // Input field component
  const InputField: React.FC<{
    label: string;
    field: string;
    type?: string;
    placeholder?: string;
    required?: boolean;
  }> = ({ label, field, type = 'text', placeholder, required = true }) => (
    <div>
      <label className="block text-sm font-medium mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        className={`w-full p-3 border-2 rounded-lg transition-colors ${
          allErrors[field] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
        }`}
        placeholder={placeholder}
        value={getFieldValue(field)}
        onChange={(e) => updateFormData(field, e.target.value)}
      />
      {allErrors[field] && (
        <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {allErrors[field]}
        </p>
      )}
    </div>
  );

  // Select field component
  const SelectField: React.FC<{
    label: string;
    field: string;
    options: { value: string; label: string }[];
    required?: boolean;
  }> = ({ label, field, options, required = true }) => (
    <div>
      <label className="block text-sm font-medium mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        className={`w-full p-3 border-2 rounded-lg transition-colors ${
          allErrors[field] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
        }`}
        value={getFieldValue(field)}
        onChange={(e) => updateFormData(field, e.target.value)}
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {allErrors[field] && (
        <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {allErrors[field]}
        </p>
      )}
    </div>
  );

  // Access type selection buttons
  const AccessTypeButton: React.FC<{
    type: 'somebodyPresent' | 'keyPickUp' | 'keySafe';
    icon: React.ReactNode;
    title: string;
    description: string;
  }> = ({ type, icon, title, description }) => (
    <button
      type="button"
      className={`border-2 p-4 rounded-lg transition-all duration-200 text-left w-full ${
        formData.accessType === type 
          ? 'shadow-md' 
          : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
      }`}
      style={{
        borderColor: formData.accessType === type ? (themes?.color1 || '#1E5188') : undefined,
        backgroundColor: formData.accessType === type ? 
          `${themes?.color1 || '#1E5188'}15` : undefined
      }}
      onClick={() => updateFormData('accessType', type)}
    >
      <div className="flex items-start gap-3">
        <div 
          className={`w-4 h-4 rounded-full border-2 flex-shrink-0 mt-1 ${
            formData.accessType === type 
              ? 'border-current' 
              : 'border-gray-300'
          }`}
          style={{
            backgroundColor: formData.accessType === type ? (themes?.color1 || '#1E5188') : 'transparent',
            borderColor: formData.accessType === type ? (themes?.color1 || '#1E5188') : undefined
          }}
        >
          {formData.accessType === type && (
            <div 
              className="w-2 h-2 rounded-full m-0.5"
              style={{ backgroundColor: themes?.fontColor || '#ffffff' }}
            ></div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-2">
            <div style={{ color: themes?.color1 || '#1E5188' }}>
              {icon}
            </div>
            <h4 className="font-semibold text-base">{title}</h4>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
        </div>
      </div>
    </button>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <Key className="w-16 h-16 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Property Access</h3>
              <p className="text-gray-600">How will our team access the property for the {serviceName}?</p>
            </div>
            
            <div className="space-y-4 max-w-2xl mx-auto">
              <AccessTypeButton
                type="somebodyPresent"
                icon={<User className="w-6 h-6" />}
                title="Somebody Present on Site"
                description="Someone will be present at the property to provide access to our team."
              />
              
              <AccessTypeButton
                type="keyPickUp"
                icon={<KeyRound className="w-6 h-6" />}
                title="Key Pick Up"
                description="Our team will collect keys from a specified location before visiting the property."
              />
              
              <AccessTypeButton
                type="keySafe"
                icon={<Shield className="w-6 h-6" />}
                title="Key Safe"
                description="Property has a key safe with a code that our team can use to access."
              />
            </div>
          </div>
        );
  
      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <Home className="w-16 h-16 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Property Details</h3>
              <p className="text-gray-600">Tell us about the property that needs the {serviceName}</p>
            </div>
            
            <div className="max-w-2xl mx-auto space-y-6">
              {/* Property Address */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold mb-4 text-gray-800">Property Address</h4>
                <div className="grid grid-cols-1 gap-4">
                  <InputField
                    label="Address Line 1"
                    field="propertyAddress.address1"
                    placeholder="e.g., 12 Downing Street"
                  />
                  
                  <InputField
                    label="Address Line 2"
                    field="propertyAddress.address2"
                    placeholder="e.g., Westminster"
                    required={false}
                  />
                  
                  <InputField
                    label="Postcode"
                    field="propertyAddress.postcode"
                    placeholder="e.g., SW1A 2AA"
                  />
                </div>
              </div>
  
              {/* Property Details */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold mb-4 text-gray-800">Property Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    label="Property Type"
                    field="propertyDetails.propertyType"
                    options={[
                      { value: 'residential', label: 'Residential' },
                      { value: 'commercial', label: 'Commercial' }
                    ]}
                  />
                  
                  <InputField
                    label="Number of Bedrooms"
                    field="propertyDetails.numberOfBedrooms"
                    type="number"
                    placeholder="e.g., 3"
                  />
                  
                  <SelectField
                    label="Customer Type"
                    field="propertyDetails.customerType"
                    options={[
                      { value: 'homeowner', label: 'Homeowner' },
                      { value: 'landlord', label: 'Landlord' },
                      { value: 'tenant', label: 'Tenant' },
                      { value: 'agent', label: 'Agent' }
                    ]}
                  />
                  
                  <SelectField
                    label="Furnished State"
                    field="propertyDetails.furnishedState"
                    options={[
                      { value: '1', label: 'Unfurnished' },
                      { value: '2', label: 'Part Furnished' },
                      { value: '3', label: 'Furnished' }
                    ]}
                  />
                </div>
                
                <div className="mt-4">
                  <InputField
                    label="Customer Name"
                    field="propertyDetails.customerName"
                    placeholder="e.g., John Smith"
                  />
                </div>
                
                <div className="mt-4">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.propertyDetails.isInhabited}
                      onChange={(e) => updateFormData('propertyDetails.isInhabited', e.target.checked)}
                      className="w-4 h-4 rounded border-2 transition-colors focus:ring-2 focus:ring-offset-2"
                      style={{
                        borderColor: formData.propertyDetails.isInhabited ? (themes?.color1 || '#1E5188') : '#d1d5db',
                        backgroundColor: formData.propertyDetails.isInhabited ? (themes?.color1 || '#1E5188') : 'transparent',
                        accentColor: themes?.color1 || '#1E5188'
                      }}
                    />
                    <span className="text-sm font-medium text-gray-700">Property is currently inhabited</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        );
  
      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <User className="w-16 h-16 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Contact Details</h3>
              <p className="text-gray-600">Who should we contact regarding this service?</p>
            </div>
            
            <div className="max-w-2xl mx-auto">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold mb-4 text-gray-800">Primary Contact (Person paying for service)</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <SelectField
                    label="Title"
                    field="contactDetails.title"
                    options={[
                      { value: '', label: 'Select...' },
                      { value: 'Mr', label: 'Mr' },
                      { value: 'Mrs', label: 'Mrs' },
                      { value: 'Miss', label: 'Miss' },
                      { value: 'Ms', label: 'Ms' },
                      { value: 'Dr', label: 'Dr' }
                    ]}
                  />
                  
                  <InputField
                    label="First Name"
                    field="contactDetails.firstName"
                    placeholder="John"
                  />
                  
                  <InputField
                    label="Surname"
                    field="contactDetails.surname"
                    placeholder="Smith"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    label="Email Address"
                    field="contactDetails.emailAddress"
                    type="email"
                    placeholder="john.smith@email.com"
                  />
                  
                  <InputField
                    label="Phone Number"
                    field="contactDetails.phoneNumber"
                    type="tel"
                    placeholder="07123456789"
                  />
                </div>
                
                <div className="mt-4 p-3 bg-blue-50 rounded-md">
                  <p className="text-sm text-blue-700">
                    <strong>Note:</strong> An email with the completed report/assessment will be sent to this contact upon completion.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
  
      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <MapPin className="w-16 h-16 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Access Arrangements</h3>
              <p className="text-gray-600">Provide details for property access</p>
            </div>
            
            <div className="max-w-2xl mx-auto space-y-6">
              {/* Show selected access type */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-2">Selected Access Method:</h4>
                <p className="text-blue-700">
                  {formData.accessType === 'somebodyPresent' && 'Somebody Present on Site'}
                  {formData.accessType === 'keyPickUp' && 'Key Pick Up'}
                  {formData.accessType === 'keySafe' && 'Key Safe'}
                </p>
              </div>
  
              {formData.accessType !== 'somebodyPresent' && (
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-semibold mb-4 text-gray-800">Property Access Contact</h4>
                  <p className="text-sm text-gray-600 mb-4">Contact details for arranging property access</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <SelectField
                      label="Title"
                      field="accessContactDetails.title"
                      options={[
                        { value: '', label: 'Select...' },
                        { value: 'Mr', label: 'Mr' },
                        { value: 'Mrs', label: 'Mrs' },
                        { value: 'Miss', label: 'Miss' },
                        { value: 'Ms', label: 'Ms' },
                        { value: 'Dr', label: 'Dr' }
                      ]}
                    />
                    
                    <InputField
                      label="First Name"
                      field="accessContactDetails.firstName"
                      placeholder="David"
                    />
                    
                    <InputField
                      label="Surname"
                      field="accessContactDetails.surname"
                      placeholder="Tenant"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <InputField
                      label="Email Address"
                      field="accessContactDetails.emailAddress"
                      type="email"
                      placeholder="david.tenant@email.com"
                    />
                    
                    <InputField
                      label="Secondary Phone (Optional)"
                      field="accessContactDetails.phoneNumber1"
                      type="tel"
                      placeholder="01234567890"
                      required={false}
                    />
                  </div>
                  
                  <InputField
                    label="Mobile Number"
                    field="accessContactDetails.phoneNumber2"
                    type="tel"
                    placeholder="07123456789"
                  />
                  
                  <div className="mt-4 p-3 bg-yellow-50 rounded-md">
                    <p className="text-sm text-yellow-700">
                      <strong>Note:</strong> Mobile number is required as we'll send a booking link via SMS.
                    </p>
                  </div>
                </div>
              )}
  
              {/* Key Pickup Details */}
              {formData.accessType === 'keyPickUp' && (
                <>
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h4 className="font-semibold mb-4 text-gray-800">Key Pickup Address</h4>
                    <div className="grid grid-cols-1 gap-4">
                      <InputField
                        label="Pickup Address Line 1"
                        field="keyPickUpAddress.address1"
                        placeholder="e.g., Buckingham Palace"
                      />
                      
                      <InputField
                        label="Pickup Address Line 2"
                        field="keyPickUpAddress.address2"
                        placeholder="e.g., London"
                        required={false}
                      />
                      
                      <InputField
                        label="Pickup Postcode"
                        field="keyPickUpAddress.postcode"
                        placeholder="e.g., SW1A 1AA"
                      />
                    </div>
                  </div>
  
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h4 className="font-semibold mb-4 text-gray-800">Key Pickup Contact</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <SelectField
                        label="Title"
                        field="keyPickUpContactDetails.title"
                        options={[
                          { value: '', label: 'Select...' },
                          { value: 'Mr', label: 'Mr' },
                          { value: 'Mrs', label: 'Mrs' },
                          { value: 'Miss', label: 'Miss' },
                          { value: 'Ms', label: 'Ms' },
                          { value: 'Dr', label: 'Dr' }
                        ]}
                      />
                      
                      <InputField
                        label="First Name"
                        field="keyPickUpContactDetails.firstName"
                        placeholder="Sue"
                      />
                      
                      <InputField
                        label="Surname"
                        field="keyPickUpContactDetails.surname"
                        placeholder="Pickup"
                      />
                      
                      <InputField
                        label="Email Address"
                        field="keyPickUpContactDetails.emailAddress"
                        type="email"
                        placeholder="sue.pickup@email.com"
                      />
                      
                      <InputField
                        label="Phone Number"
                        field="keyPickUpContactDetails.phoneNumber"
                        type="tel"
                        placeholder="07111111111"
                      />
                    </div>
                  </div>
                </>
              )}
  
              {/* Key Safe Details */}
              {formData.accessType === 'keySafe' && (
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-semibold mb-4 text-gray-800">Key Safe Information</h4>
                  <InputField
                    label="Key Safe Code"
                    field="keyCode"
                    placeholder="e.g., 123456"
                  />
                  <div className="mt-4 p-3 bg-green-50 rounded-md">
                    <p className="text-sm text-green-700">
                      <strong>Security Note:</strong> The key safe code will be securely transmitted to our team.
                    </p>
                  </div>
                </div>
              )}
  
              {/* If somebody present, show simple message */}
              {formData.accessType === 'somebodyPresent' && (
                <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-2">Access Confirmed</h4>
                  <p className="text-green-700">
                    Someone will be present at the property to provide access to our team. 
                    We'll contact you using the details provided in the previous step to arrange a suitable time.
                  </p>
                </div>
              )}
            </div>
          </div>
        );
  
      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <Calendar className="w-16 h-16 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Appointment & Additional Information</h3>
              <p className="text-gray-600">When would you like this service completed?</p>
            </div>
            
            <div className="max-w-2xl mx-auto space-y-6">
              {/* Appointment Details */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold mb-4 text-gray-800">Appointment Preference</h4>
                
                <div className="space-y-4">
                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      name="appointmentType"
                      checked={formData.appointmentDetails.asap}
                      onChange={() => updateFormData('appointmentDetails.asap', true)}
                      className="w-4 h-4 rounded border-2 transition-colors focus:ring-2 focus:ring-offset-2"
                      style={{
                        borderColor: formData.appointmentDetails.asap ? (themes?.color1 || '#1E5188') : '#d1d5db',
                        backgroundColor: formData.appointmentDetails.asap ? (themes?.color1 || '#1E5188') : 'transparent',
                        accentColor: themes?.color1 || '#1E5188'
                      }}
                    />
                    <span className="text-sm font-medium text-gray-700">As soon as possible (ASAP)</span>
                  </label>
                  
                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      name="appointmentType"
                      checked={!formData.appointmentDetails.asap}
                      onChange={() => updateFormData('appointmentDetails.asap', false)}
                      className="w-4 h-4 rounded border-2 transition-colors focus:ring-2 focus:ring-offset-2"
                      style={{
                        borderColor: !formData.appointmentDetails.asap ? (themes?.color1 || '#1E5188') : '#d1d5db',
                        backgroundColor: !formData.appointmentDetails.asap ? (themes?.color1 || '#1E5188') : 'transparent',
                        accentColor: themes?.color1 || '#1E5188'
                      }}
                    />
                    <span className="text-sm font-medium text-gray-700">Preferred date/time</span>
                  </label>
                </div>
                
                {!formData.appointmentDetails.asap && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Preferred Date
                      </label>
                      <input
                        type="date"
                        className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 transition-colors"
                        value={formData.appointmentDetails.preferredDate || ''}
                        onChange={(e) => updateFormData('appointmentDetails.preferredDate', e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Preferred Time
                      </label>
                      <select
                        className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 transition-colors"
                        value={formData.appointmentDetails.preferredTime || ''}
                        onChange={(e) => updateFormData('appointmentDetails.preferredTime', e.target.value)}
                      >
                        <option value="">Select time...</option>
                        <option value="morning">Morning (9 AM - 12 PM)</option>
                        <option value="afternoon">Afternoon (12 PM - 5 PM)</option>
                        <option value="evening">Evening (5 PM - 8 PM)</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
  
              {/* Additional Notes */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold mb-4 text-gray-800">Additional Notes</h4>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Special Instructions or Comments
                  </label>
                  <textarea
                    className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 transition-colors"
                    rows={4}
                    placeholder="Any special instructions, access notes, or additional information..."
                    value={formData.notes}
                    onChange={(e) => updateFormData('notes', e.target.value)}
                  />
                </div>
              </div>
  
              {/* Summary Information */}
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                <h4 className="font-semibold mb-4 text-blue-800">Booking Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service:</span>
                    <span className="font-medium">{serviceName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Property:</span>
                    <span className="font-medium">
                      {formData.propertyAddress.address1}, {formData.propertyAddress.postcode}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Access Method:</span>
                    <span className="font-medium">
                      {formData.accessType === 'somebodyPresent' && 'Somebody Present on Site'}
                      {formData.accessType === 'keyPickUp' && 'Key Pick Up'}
                      {formData.accessType === 'keySafe' && 'Key Safe'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Contact:</span>
                    <span className="font-medium">
                      {formData.contactDetails.firstName} {formData.contactDetails.surname}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Appointment:</span>
                    <span className="font-medium">
                      {formData.appointmentDetails.asap ? 'As Soon As Possible' : 'Preferred Date/Time'}
                    </span>
                  </div>
                </div>
              </div>
  
              {/* Final confirmation notice */}
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <div className="flex items-start space-x-3">
                  <div className="text-yellow-600 mt-1">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h5 className="font-medium text-yellow-800">Please Review Before Submitting</h5>
                    <p className="text-sm text-yellow-700 mt-1">
                      Once you submit this booking request, our team will contact you to confirm the appointment and provide further details about the {serviceName} service.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
  
      default:
        return null;
    }
  };

  return (
    <div className="p-6 space-y-6 h-full flex flex-col">
      {/* Progress Indicator */}
      <div className="flex items-center justify-between mb-6">
        {[1, 2, 3, 4, 5].map((step) => (
          <div key={step} className="flex items-center">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                step <= currentStep
                  ? 'shadow-lg'
                  : 'bg-gray-200 text-gray-600'
              }`}
              style={{
                backgroundColor: step <= currentStep ? (themes?.color1 || '#1E5188') : undefined,
                color: step <= currentStep ? (themes?.fontColor || '#ffffff') : undefined
              }}
            >
              {step < currentStep ? '✓' : step}
            </div>
            {step < 5 && (
              <div
                className={`w-16 h-2 mx-2 rounded-full transition-all ${
                  step < currentStep ? 'shadow-sm' : 'bg-gray-200'
                }`}
                style={{
                  backgroundColor: step < currentStep ? (themes?.color1 || '#1E5188') : undefined
                }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Form Content - Scrollable */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {renderStep()}
      </div>

      {/* Navigation Buttons - Fixed at bottom */}
      <div className="flex justify-between pt-6 border-t bg-white">
        <button
          type="button"
          onClick={currentStep === 1 ? onCancel : handleBack}
          disabled={isSubmitting}
          className="px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors font-medium"
        >
          {currentStep === 1 ? 'Cancel' : '← Back'}
        </button>

        <button
          type="button"
          onClick={currentStep === 5 ? handleSubmit : handleNext}
          disabled={isSubmitting}
          className="px-6 py-3 rounded-lg disabled:opacity-50 transition-all font-bold shadow-lg hover:shadow-xl transform hover:scale-105"
          style={{ 
            backgroundColor: themes?.color1 || '#1E5188',
            color: themes?.fontColor || '#ffffff'
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.backgroundColor = themes?.color2 || themes?.color1 || '#1E5188';
            }
          }}
          onMouseLeave={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.backgroundColor = themes?.color1 || '#1E5188';
            }
          }}
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <Loader2 className="animate-spin h-4 w-4" />
              Processing...
            </span>
          ) : currentStep === 5 ? (
            'Submit Booking Request'
          ) : (
            'Next →'
          )}
        </button>
      </div>
    </div>
  );
};

export default AdditionalProductForm;